import type {Page} from '@PosterWhiteboard/page/page.class';
import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import {v4 as uuidv4} from 'uuid';

export class PageGroups {
  private page: Page;

  public constructor(page: Page) {
    this.page = page;
  }

  public getItemsByGroupId(groupId: string): ItemType[] {
    return Object.values(this.page.items.itemsHashMap).filter((item) => item.groupId === groupId);
  }

  public getExclusivelySelectedGroupId(): string | undefined {
    const selectedItems = this.page.getSelectedZIndexMovableItems();
    if (selectedItems.length < 2) {
      return undefined;
    }
    const firstGroupId = selectedItems[0].groupId;
    if (!firstGroupId) {
      return undefined;
    }
    if (!selectedItems.every((item) => item.groupId === firstGroupId)) {
      return undefined;
    }
    const memberCount = this.getItemsByGroupId(firstGroupId).length;
    if (selectedItems.length !== memberCount) {
      return undefined;
    }
    return firstGroupId;
  }

  public isSelectionExactlyOneGroup(): boolean {
    return this.getExclusivelySelectedGroupId() !== undefined;
  }

  public getGroupZRange(groupId: string): {bottomZ: number; topZ: number} | undefined {
    const objs = this.page.fabricCanvas.getObjects();
    const indices = this.getItemsByGroupId(groupId)
      .map((m) => objs.indexOf(m.fabricObject))
      .filter((i) => i >= 0);
    if (indices.length === 0) {
      return undefined;
    }
    return {bottomZ: Math.min(...indices), topZ: Math.max(...indices)};
  }

  public groupSelection(): void {
    const selectedItems = this.page.getSelectedZIndexMovableItems();
    if (selectedItems.length < 2) {
      return;
    }
    this.groupItems(selectedItems);
  }

  public ungroupSelection(): void {
    const selectedItems = this.page.getSelectedItems();
    if (selectedItems.length === 1 && selectedItems[0].groupId) {
      this.removeItemFromGroup(selectedItems[0]);
      return;
    }

    const exclusiveGroupId = this.getExclusivelySelectedGroupId();
    if (exclusiveGroupId) {
      this.ungroupItems(exclusiveGroupId);
    }
  }

  public groupItems(items: ItemType[]): string | undefined {
    if (items.length < 2) {
      return undefined;
    }

    const groupId = uuidv4();
    for (const item of items) {
      item.groupId = groupId;
    }

    this.enforceGroupContiguity(groupId);
    this.page.groupDrillIn.enableSubTargetCheck();
    this.page.poster.itemsMultiSelect.updateFromObject({enabled: false});

    this.page.poster.history.addPosterHistory();
    this.page.poster.redux.updateReduxData();
    this.page.fire('modified');

    return groupId;
  }

  public ungroupItems(groupId: string): void {
    const members = this.getItemsByGroupId(groupId);
    if (members.length === 0) {
      return;
    }

    for (const member of members) {
      member.groupId = null;
    }

    this.page.poster.itemsMultiSelect.updateFromObject({enabled: false});

    this.page.poster.history.addPosterHistory();
    this.page.poster.redux.updateReduxData();
    this.page.fire('modified');
  }

  public removeItemFromGroup(item: ItemType): void {
    if (!item.groupId) {
      return;
    }
    const groupId = item.groupId;
    item.groupId = null;

    const remainingMembers = this.getItemsByGroupId(groupId);
    if (remainingMembers.length >= 2) {
      const canvas = this.page.fabricCanvas;
      const objs = canvas.getObjects();
      const aIndex = objs.indexOf(item.fabricObject);
      const maxRemaining = Math.max(...remainingMembers.map((m) => objs.indexOf(m.fabricObject)));
      // Fabric's moveObjectTo with target >= source lands at exactly that slot after its
      // remove+insert reindex — placing the leaver one above the topmost remaining member.
      if (aIndex >= 0 && aIndex < maxRemaining) {
        canvas.moveObjectTo(item.fabricObject, maxRemaining);
      }
    }

    this.enforceGroupContiguity(groupId);
    this.autoDissolveIfTooSmall(groupId);

    this.page.poster.history.addPosterHistory();
    this.page.poster.redux.updateReduxData();
    this.page.fire('modified');
  }

  public repairAllGroupContiguity(): void {
    const seen = new Set<string>();
    for (const item of Object.values(this.page.items.itemsHashMap)) {
      if (item.groupId && !seen.has(item.groupId)) {
        seen.add(item.groupId);
        this.enforceGroupContiguity(item.groupId);
      }
    }
  }

  private enforceGroupContiguity(groupId: string): void {
    const members = this.getItemsByGroupId(groupId);
    if (members.length < 2) {
      return;
    }

    const canvas = this.page.fabricCanvas;
    const canvasObjects = canvas.getObjects();

    const memberIndices = members
      .map((item) => ({item, index: canvasObjects.indexOf(item.fabricObject)}))
      .filter(({index}) => index >= 0)
      .sort((a, b) => a.index - b.index);

    if (memberIndices.length < 2) {
      return;
    }

    const frontmostIndex = memberIndices[memberIndices.length - 1].index;

    // Top-down iteration is load-bearing: moving frontmost-of-the-rest first avoids
    // intermediate states where a non-member gets wedged between members.
    for (let i = memberIndices.length - 2; i >= 0; i--) {
      const offset = memberIndices.length - 1 - i;
      canvas.moveObjectTo(memberIndices[i].item.fabricObject, frontmostIndex - offset);
    }
  }

  public autoDissolveIfTooSmall(groupId: string): void {
    const members = this.getItemsByGroupId(groupId);
    if (members.length >= 2) {
      return;
    }

    for (const member of members) {
      member.groupId = null;
    }
  }
}
